import { preloadRoute } from 'utils/lazyRoute';
import { ROUTES } from 'utils/constants';

type RoutePreloadMatcher = {
  match: (pathname: string) => boolean;
  preload: () => Promise<unknown>;
};

const AUTHENTICATED_ROUTE_PRELOADERS: RoutePreloadMatcher[] = [
  {
    match: (p) => p === ROUTES.DASHBOARD,
    preload: () => import('pages/dashboard'),
  },
  {
    match: (p) =>
      p === ROUTES.TAX_COMPLIANCE || p.startsWith(`${ROUTES.TAX_COMPLIANCE}/`),
    preload: () => import('pages/taxCompliance'),
  },
  {
    match: (p) => p === ROUTES.BUSINESS_CALENDAR_ACTION_ITEMS,
    preload: () =>
      import('pages/businessCalendar/BusinessCalendarActionItemsPage'),
  },
  {
    match: (p) =>
      p === ROUTES.BUSINESS_CALENDAR ||
      p.startsWith(`${ROUTES.BUSINESS_CALENDAR}/`),
    preload: () => import('pages/businessCalendar'),
  },
  {
    match: (p) =>
      p === ROUTES.UPLOAD_ENTRY ||
      p === ROUTES.UPLOAD_ENTRY_PROCESS ||
      p.startsWith(`${ROUTES.UPLOAD_ENTRY}/`),
    preload: () => import('pages/uploadEntry'),
  },
  {
    match: (p) =>
      p === ROUTES.CATEGORIES.ROOT ||
      p.startsWith(`${ROUTES.CATEGORIES.ROOT}/`),
    preload: () => import('pages/categories'),
  },
  {
    match: (p) =>
      p === ROUTES.JOURNALS.ROOT || p.startsWith(`${ROUTES.JOURNALS.ROOT}/`),
    preload: () => import('pages/journals'),
  },
  {
    match: (p) => p === ROUTES.JOURNALS.CUSTOMER_EMAIL_RECENT_SENDS,
    preload: () => import('pages/customers/CustomerEmailRecentSendsPage'),
  },
  {
    match: (p) => p === ROUTES.JOURNALS.CUSTOMER_EMAIL,
    preload: () => import('pages/customers/CustomerEmailPage'),
  },
  {
    match: (p) => p === ROUTES.JOURNALS.BILL_INVOICE,
    preload: () => import('pages/customers/billInvoiceSettings'),
  },
  {
    match: (p) => p === ROUTES.JOURNALS.CUSTOMERS,
    preload: () => import('pages/customers'),
  },
  {
    match: (p) => p === ROUTES.JOURNALS.SUPPLIERS,
    preload: () => import('pages/suppliers'),
  },
  {
    match: (p) => p === ROUTES.REPORTS.ROOT,
    preload: () => import('pages/reports'),
  },
  {
    match: (p) => p === ROUTES.REPORTS.INCOME_STATEMENT,
    preload: () => import('pages/reports/IncomeStatement'),
  },
  {
    match: (p) => p === ROUTES.REPORTS.BALANCE_SHEET,
    preload: () => import('pages/reports/BalanceSheet'),
  },
  {
    match: (p) => p === ROUTES.REPORTS.CASH_FLOW_STATEMENT,
    preload: () => import('pages/reports/CashFlowStatement'),
  },
  {
    match: (p) => p === ROUTES.REPORTS.STATEMENT_OF_CHANGES_IN_EQUITY,
    preload: () => import('pages/reports/StatementOfChangesInEquity'),
  },
  {
    match: (p) => p === ROUTES.REPORTS.FINANCIAL_STATEMENT,
    preload: () => import('pages/reports/FinancialStatementGeneration'),
  },
  {
    match: (p) => p === ROUTES.REPORTS.GENERAL_LEDGER,
    preload: () => import('pages/reports/GeneralLedger'),
  },
  {
    match: (p) => p === ROUTES.REPORTS.TRIAL_BALANCE,
    preload: () => import('pages/reports/TrialBalance'),
  },
  {
    match: (p) => p === ROUTES.REPORTS.GENERAL_JOURNAL,
    preload: () => import('pages/reports/GeneralJournal'),
  },
  {
    match: (p) => p === ROUTES.TRANSACTIONS.ADD,
    preload: () => import('pages/transactions/Add'),
  },
  {
    match: (p) =>
      p.startsWith(`${ROUTES.TRANSACTIONS.ROOT}/`) && p.endsWith('/edit'),
    preload: () => import('pages/transactions/Edit'),
  },
  {
    match: (p) =>
      p.startsWith(`${ROUTES.TRANSACTIONS.ROOT}/`) &&
      p !== ROUTES.TRANSACTIONS.ADD &&
      !p.endsWith('/edit'),
    preload: () => import('pages/transactions/View'),
  },
  {
    match: (p) => p === ROUTES.TRANSACTIONS.ROOT,
    preload: () => import('pages/transactions'),
  },
  {
    match: (p) => p === ROUTES.CASH_RECEIPTS_JOURNAL.ROOT,
    preload: () => import('pages/cashReceiptsJournal/CashReceiptsJournalList'),
  },
  {
    match: (p) => p === ROUTES.CASH_DISBURSEMENT_JOURNAL.ROOT,
    preload: () =>
      import('pages/cashDisbursementJournal/CashDisbursementJournalList'),
  },
  {
    match: (p) => p === ROUTES.SETTINGS.GENERAL,
    preload: () => import('pages/settings/GeneralSettings'),
  },
  {
    match: (p) => p === ROUTES.SETTINGS.NOTIFICATIONS,
    preload: () => import('pages/settings/NotificationSettings'),
  },
  {
    match: (p) => p === ROUTES.SETTINGS.PROFILE,
    preload: () => import('pages/user/Profile'),
  },
  {
    match: (p) => p === ROUTES.SETTINGS.USERS,
    preload: () => import('pages/company/Users'),
  },
  {
    match: (p) => p === ROUTES.SETTINGS.COMPANY_SETTINGS,
    preload: () => import('pages/settings/CompanySettings'),
  },
  {
    match: (p) =>
      p === ROUTES.SETTINGS.COMPANIES || p === ROUTES.COMPANIES.ROOT,
    preload: () => import('pages/companies'),
  },
  {
    match: (p) => p === ROUTES.USER.PROFILE,
    preload: () => import('pages/user/Profile'),
  },
  {
    match: (p) => p === ROUTES.SETTINGS.TRANSACTION_PERIOD_LOCKS,
    preload: () => import('pages/company/TransactionPeriodLocks'),
  },
  {
    match: (p) => p === ROUTES.DOWNLOADABLES,
    preload: () => import('pages/downloadables'),
  },
  {
    match: (p) => p === ROUTES.HELP || p.startsWith(`${ROUTES.HELP}/`),
    preload: () => import('pages/help'),
  },
  {
    match: (p) => p.startsWith(`${ROUTES.HELP}/how-to/`),
    preload: () => import('pages/help/HelpHowToTopic'),
  },
  {
    match: (p) => p === ROUTES.HELP_CUSTOMER_EMAIL_EDITOR,
    preload: () => import('pages/help/CustomerEmailEditorHelp'),
  },
  {
    match: (p) => p === ROUTES.HELP_EQUIPMENT,
    preload: () => import('pages/help/EquipmentHelp'),
  },
  {
    match: (p) => p === ROUTES.HELP_POS_INVENTORY,
    preload: () => import('pages/help/PosInventoryHelp'),
  },
  {
    match: (p) => p === ROUTES.USER.ONBOARDING,
    preload: () => import('pages/user/Onboarding'),
  },
];

const warmedPaths = new Set<string>();

/** Prefetch route chunks for a path (deduped per session). */
export function prefetchAuthenticatedRoute(pathname: string): void {
  const path = pathname.split('?')[0] ?? pathname;
  if (warmedPaths.has(path)) {
    return;
  }

  for (const { match, preload } of AUTHENTICATED_ROUTE_PRELOADERS) {
    if (match(path)) {
      warmedPaths.add(path);
      preloadRoute(preload);
      return;
    }
  }
}

/** Idle-time warmup for the most common destinations after login. */
export function prefetchCommonAuthenticatedRoutes(): void {
  const commonPaths = [
    ROUTES.DASHBOARD,
    ROUTES.CATEGORIES.ROOT,
    ROUTES.JOURNALS.ROOT,
    ROUTES.REPORTS.ROOT,
    ROUTES.TRANSACTIONS.ROOT,
    ROUTES.CASH_RECEIPTS_JOURNAL.ROOT,
    ROUTES.CASH_DISBURSEMENT_JOURNAL.ROOT,
    ROUTES.UPLOAD_ENTRY,
    ROUTES.SETTINGS.GENERAL,
    ROUTES.SETTINGS.COMPANIES,
    ROUTES.SETTINGS.PROFILE,
  ];

  for (const path of commonPaths) {
    prefetchAuthenticatedRoute(path);
  }
}
